import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { PlusIcon } from "lucide-react";
import ConversationTreesTable from "./ConversationTreesTable";
import { SortConfig } from "../ui/paginated-table";
import { ConversationBuilderService } from "../../services/ConversationBuilderService";
import { toast } from "sonner";
import { Modal } from "antd";

// Define the interface inline as per codebase patterns
interface ConversationTree {
  id: number;
  conversation_flow_name: string;
  conversation_flow_version: number;
  is_latest: boolean;
  created_at: string;
  application_name: string;
  intent_name?: string;
  conversation_flow_count?: number;
}

const ConversationTrees: React.FC = () => {
  const { apiKey, companyId } = usePermissions();
  const [conversationTrees, setConversationTrees] = useState<
    ConversationTree[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig<ConversationTree>>({
    key: null,
    direction: "desc",
  });
  const pageSize = 10;

  const navigate = useNavigate();

  useEffect(() => {
    refreshConversationTrees();
  }, [apiKey, companyId]);

  const refreshConversationTrees = () => {
    if (!companyId || !apiKey) return;

    setIsLoading(true);

    ConversationBuilderService.listLatestConversationTreesByCompany(
      companyId,
      apiKey
    ).subscribe({
      next: (response: ConversationTree[]) => {
        if (response && Array.isArray(response)) {
          setConversationTrees(response);
        } else {
          console.error("Invalid response format:", response);
          setConversationTrees([]);
        }
        setIsLoading(false);
      },
      error: (error: any) => {
        console.error("Error loading conversation trees:", error);
        setIsLoading(false);
      },
    });
  };

  const handleSort = (key: keyof ConversationTree) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleEdit = (tree: ConversationTree) => {
    // Navigate to Execute page for this conversation tree
    navigate(`/forge/conversation-trees/${tree.id}/execute`);
  };

  const handleCreateNew = () => {
    // Navigate to conversation tree builder for new tree with new=true parameter
    navigate("/forge/conversation-tree?new=true");
  };

  const handleDelete = (tree: ConversationTree) => {
    Modal.confirm({
      title: "Delete Conversation Tree",
      content: (
        <div>
          <p>
            Are you sure you want to delete the conversation tree "
            <strong>{tree.conversation_flow_name}</strong>"?
          </p>
          <p className="text-red-600 mt-2">
            This will permanently delete ALL versions of this conversation tree
            and cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      onOk: () => {
        if (!apiKey) return;

        setIsLoading(true);
        ConversationBuilderService.deleteConversationTree(
          tree.id,
          apiKey
        ).subscribe({
          next: () => {
            toast.success(
              `Successfully deleted conversation tree "${tree.conversation_flow_name}"`
            );
            refreshConversationTrees();
          },
          error: (error) => {
            console.error("Error deleting conversation tree:", error);
            toast.error(error?.message || "Failed to delete conversation tree");
            setIsLoading(false);
          },
        });
      },
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    Conversation Trees
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Conversation Trees
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage and execute your conversation tree configurations
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end">
          <Button onClick={handleCreateNew} className="gap-2">
            <PlusIcon className="w-4 h-4" />
            Create New Tree
          </Button>
        </div>

        {/* Table */}
        <ConversationTreesTable
          conversationTrees={conversationTrees}
          loading={isLoading}
          sortConfig={sortConfig}
          onSort={handleSort}
          onEdit={handleEdit}
          onDelete={handleDelete}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
        />
      </div>
    </div>
  );
};

export default ConversationTrees;
